import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const WishListSection = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [expandedItems, setExpandedItems] = useState({});

  // ✅ NEW: Image states for each wishlist item
  const [itemImages, setItemImages] = useState({}); // { wishlist_item_id: { file, preview, uploadedId } }
  const [uploadingImages, setUploadingImages] = useState({}); // { wishlist_item_id: boolean }

  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const IMG_BASE_URL = process.env.REACT_APP_IMG_BASE_URL;

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'assets/images/thumbs/default-material.png';
    if (imageUrl.startsWith('http')) return imageUrl;
    if (imageUrl.startsWith('/')) return `${IMG_BASE_URL}${imageUrl}`;
    return `${IMG_BASE_URL}${imageUrl}`;
  };

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/wishlist/my-wishlist/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      const items = response.data.items || [];
      setWishlistItems(items);

      const total = items.reduce((sum, item) => sum + parseFloat(item.prices?.total_price || 0), 0);
      setTotalPrice(total);

      setError(null);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      if (error.response?.status === 401) {
        setError('Please login to view your wishlist');
      } else {
        setError('Failed to load wishlist');
      }
      setWishlistItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ NEW: Handle image selection for wishlist item
  const handleImageChange = (e, wishlistItemId) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setItemImages(prev => ({
          ...prev,
          [wishlistItemId]: {
            file: file,
            preview: reader.result,
            uploadedId: null
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // ✅ NEW: Remove image from wishlist item
  const removeItemImage = (wishlistItemId) => {
    setItemImages(prev => {
      const updated = { ...prev };
      delete updated[wishlistItemId];
      return updated;
    });
  };

  // ✅ NEW: Upload image to backend
  const uploadImage = async (wishlistItemId) => {
    const imageData = itemImages[wishlistItemId];
    if (!imageData || !imageData.file) return null;

    setUploadingImages(prev => ({ ...prev, [wishlistItemId]: true }));

    try {
      const formData = new FormData();
      formData.append('image', imageData.file);

      const response = await axios.post(
        `${API_BASE_URL}/customization-images/`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const uploadedId = response.data.data.image_id;

        // Update itemImages with uploaded ID
        setItemImages(prev => ({
          ...prev,
          [wishlistItemId]: {
            ...prev[wishlistItemId],
            uploadedId: uploadedId
          }
        }));

        return uploadedId;
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImages(prev => ({ ...prev, [wishlistItemId]: false }));
    }
    return null;
  };

  const removeFromWishlist = async (itemId) => {
    if (!window.confirm('Are you sure you want to remove this item?')) return;

    try {
      await axios.delete(
        `${API_BASE_URL}/wishlist/remove-item/${itemId}/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      const updatedItems = wishlistItems.filter(item => item.wishlist_item_id !== itemId);
      setWishlistItems(updatedItems);

      const newTotal = updatedItems.reduce((sum, item) => sum + parseFloat(item.prices?.total_price || 0), 0);
      setTotalPrice(newTotal);

      // Remove image data for this item
      removeItemImage(itemId);

      alert("✅ Removed from wishlist");
    } catch (error) {
      console.error('Error removing item:', error);
      alert("Failed to remove from wishlist");
    }
  };

  const removeFromWishlistAfterCart = async (itemId) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/wishlist/remove-item/${itemId}/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      const updatedItems = wishlistItems.filter(item => item.wishlist_item_id !== itemId);
      setWishlistItems(updatedItems);

      const newTotal = updatedItems.reduce((sum, item) =>
        sum + parseFloat(item.prices?.total_price || 0), 0
      );
      setTotalPrice(newTotal);

      // Remove image data
      removeItemImage(itemId);

    } catch (error) {
      console.error('Error removing item from wishlist:', error);
    }
  };

  const removeMultipleFromWishlist = async (itemIds) => {
    try {
      await Promise.all(
        itemIds.map(itemId =>
          axios.delete(
            `${API_BASE_URL}/wishlist/remove-item/${itemId}/`,
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            }
          )
        )
      );

      const updatedItems = wishlistItems.filter(
        item => !itemIds.includes(item.wishlist_item_id)
      );
      setWishlistItems(updatedItems);

      const newTotal = updatedItems.reduce((sum, item) =>
        sum + parseFloat(item.prices?.total_price || 0), 0
      );
      setTotalPrice(newTotal);

      // Remove all image data for removed items
      itemIds.forEach(id => removeItemImage(id));

    } catch (error) {
      console.error('Error removing multiple items:', error);
    }
  };

  const editItem = (item) => {
    try {
      const customizationData = {};
      if (item.customizations?.selections) {
        Object.keys(item.customizations.selections).forEach(fieldId => {
          customizationData[fieldId] = item.customizations.selections[fieldId].id;
        });
      }

      const customizationString = encodeURIComponent(JSON.stringify(customizationData));

      navigate(
        `/product-details-material?type=${item.product_type.id}&material=${item.material.id}&wishlist_item_id=${item.wishlist_item_id}&customizations=${customizationString}`
      );
    } catch (error) {
      console.error('Error editing item:', error);
      alert('Failed to edit item');
    }
  };

  // ✅ UPDATED: Add to cart with customization image
  const addToCart = async (item) => {
    try {
      const accessToken = localStorage.getItem('access_token');

      if (!accessToken) {
        alert("❌ Please login to add items to cart");
        navigate('/profile');
        return;
      }

      // Upload image if present
      let customizationImageId = null;
      const imageData = itemImages[item.wishlist_item_id];

      if (imageData) {
        if (imageData.uploadedId) {
          customizationImageId = imageData.uploadedId;
        } else if (imageData.file) {
          customizationImageId = await uploadImage(item.wishlist_item_id);
          if (!customizationImageId) {
            alert("Failed to upload customization image");
            return;
          }
        }
      }

      const cartData = {
        material_id: item.material.id,
        product_type_id: item.product_type.id,
        quantity: 1,
        customization_image_id: customizationImageId, // ✅ Include image ID
        customizations: {
          width: item.customizations?.width || 36,
          height: item.customizations?.height || 24,
          fields: item.customizations?.fields || [],
          selections: item.customizations?.selections || {}
        },
        prices: {
          base_price: item.prices?.base_price || 0,
          material_price: item.prices?.material_price || 0,
          customization_price: item.prices?.customization_price || 0,
          total_price: item.prices?.total_price || 0
        }
      };

      const response = await axios.post(
        `${API_BASE_URL}/cart/add-item/`,
        cartData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        await removeFromWishlistAfterCart(item.wishlist_item_id);
        alert("✅ Added to cart and removed from wishlist!");
        navigate('/cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert("Failed to add to cart: " + (error.response?.data?.error || error.message));
    }
  };

  // ✅ UPDATED: Add all to cart with images
  const addAllToCart = async () => {
    try {
      const accessToken = localStorage.getItem('access_token');

      if (!accessToken) {
        alert("❌ Please login to add items to cart");
        navigate('/profile');
        return;
      }

      let addedCount = 0;
      const itemsToRemove = [];
      const errors = [];

      for (const item of wishlistItems) {
        try {
          // Upload image if present
          let customizationImageId = null;
          const imageData = itemImages[item.wishlist_item_id];

          if (imageData) {
            if (imageData.uploadedId) {
              customizationImageId = imageData.uploadedId;
            } else if (imageData.file) {
              customizationImageId = await uploadImage(item.wishlist_item_id);
            }
          }

          const cartData = {
            material_id: item.material.id,
            product_type_id: item.product_type.id,
            quantity: 1,
            customization_image_id: customizationImageId, // ✅ Include image ID
            customizations: {
              width: item.customizations?.width || 36,
              height: item.customizations?.height || 24,
              fields: item.customizations?.fields || [],
              selections: item.customizations?.selections || {}
            },
            prices: {
              base_price: item.prices?.base_price || 0,
              material_price: item.prices?.material_price || 0,
              customization_price: item.prices?.customization_price || 0,
              total_price: item.prices?.total_price || 0
            }
          };

          const response = await axios.post(
            `${API_BASE_URL}/cart/add-item/`,
            cartData,
            {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.data.success) {
            addedCount++;
            itemsToRemove.push(item.wishlist_item_id);
          }
        } catch (itemError) {
          errors.push(`Failed to add ${item.product_type.name}`);
        }
      }

      if (addedCount > 0) {
        await removeMultipleFromWishlist(itemsToRemove);

        let message = `✅ Added ${addedCount} item(s) to cart!`;
        if (errors.length > 0) {
          message += `\n⚠️ Failed: ${errors.length}`;
        }
        alert(message);
        navigate('/cart');
      } else {
        alert("Failed to add items to cart");
      }
    } catch (error) {
      console.error('Error adding all to cart:', error);
      alert("Failed to add items to cart");
    }
  };

  const toggleExpandCustomizations = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getCustomizationPreview = (customizations) => {
    if (!customizations) return [];

    const preview = [];

    if (customizations.width && customizations.height) {
      preview.push({
        label: 'Size',
        value: `${customizations.width}" × ${customizations.height}"`,
        id: 'size'
      });
    }

    if (customizations.fields && customizations.fields.length > 0) {
      customizations.fields.slice(0, 2).forEach((field, idx) => {
        preview.push({
          label: field.name,
          value: field.selected_value || 'N/A',
          id: `field-${idx}`
        });
      });
    }

    return preview;
  };

  const getTotalCustomizationCount = (customizations) => {
    let count = 1;
    if (customizations?.fields) {
      count += customizations.fields.length;
    }
    return count;
  };

  if (isLoading) {
    return (
      <section className="cart py-80">
        <div className="container container-lg">
          <div className="text-center">
            <div className="spinner mb-16"></div>
            <p>Loading your wishlist...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="cart py-80">
        <div className="container container-lg">
          <div className="text-center">
            <i className="ph ph-warning-circle text-48 text-danger-600 mb-16 d-block"></i>
            <p className="text-danger-600 mb-20">{error}</p>
            <Link to="/profile" className="btn btn-main">Login to View Wishlist</Link><br/><br/><br/><br/><br/><br/><br/><br/><br/>
          </div>
        </div>
      </section>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <section className="cart py-80">
        <div className="container container-lg">
          <div className="text-center">
            <i className="ph ph-heart text-48 text-orange-600 mb-16 d-block"></i>
            <h3 className="text-2xl fw-bold mb-8">Your Wishlist is Empty</h3><br/><br/><br/><br/><br/><br/><br/>
            <p className="text-gray-600 mb-20">Start adding items to your wishlist!</p>
            <Link to="/" className="btn btn-main">Continue Shopping</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="cart py-80">
      <div className="container container-lg">
        <div className="row gy-4">
          <div className="col-lg-12">
            <div className="cart-table border border-gray-100 rounded-8">
              <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                <table className="table rounded-8 overflow-hidden">
                  <thead>
                    <tr className="border-bottom border-neutral-100">
                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Delete</th>
                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Product & Material</th>
                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Customizations</th>
                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Price Breakdown</th>
                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32 border-end border-neutral-100">Customization Image</th>

                      <th className="h6 mb-0 text-lg fw-bold px-40 py-32">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wishlistItems.map((item) => {
                      const isExpanded = expandedItems[item.wishlist_item_id];
                      const previewCustomizations = getCustomizationPreview(item.customizations);
                      const totalCustomizations = getTotalCustomizationCount(item.customizations);
                      const hiddenCount = totalCustomizations - previewCustomizations.length;
                      const imageData = itemImages[item.wishlist_item_id];
                      const isUploading = uploadingImages[item.wishlist_item_id];

                      return (
                        <tr key={item.wishlist_item_id} className="border-bottom border-neutral-100">
                          <td className="px-40 py-32 border-end border-neutral-100">
                            <button
                              type="button"
                              className="remove-tr-btn flex-align gap-12 hover-text-danger-600"
                              onClick={() => removeFromWishlist(item.wishlist_item_id)}
                              title="Remove from wishlist"
                            >
                              <i className="ph ph-x-circle text-2xl d-flex" />
                              Remove
                            </button>
                          </td>

                          <td className="px-40 py-32 border-end border-neutral-100">
                            <div className="table-product d-flex align-items-center gap-24">
                              <Link
                                to={`/product-details-material?type=${item.product_type.id}&material=${item.material.id}`}
                                className="table-product__thumb border border-gray-100 rounded-8 flex-center"
                              >
                                <img
                                  src={getImageUrl(item.material.image_swatch)}
                                  alt={item.material.name}
                                />
                              </Link>
                              <div className="table-product__content text-start">
                                <h6 className="title text-lg fw-semibold mb-8">
                                  <Link
                                    to={`/product-details-material?type=${item.product_type.id}&material=${item.material.id}`}
                                    className="link text-line-2"
                                  >
                                    {item.product_type.name}
                                  </Link>
                                </h6>
                                <p className="text-sm text-gray-600 mb-0">{item.material.name}</p>
                              </div>
                            </div>
                          </td>



                          <td className="px-40 py-32 border-end border-neutral-100">
                            <div className="customizations-display">
                              {item.customizations ? (
                                <>
                                  <div className="customizations-preview">
                                    {previewCustomizations.map((custom) => (
                                      <div key={custom.id} className="custom-row">
                                        <span className="custom-label">{custom.label}:</span>
                                        <span className="custom-value">{custom.value}</span>
                                      </div>
                                    ))}
                                  </div>

                                  {isExpanded && hiddenCount > 0 && (
                                    <div className="customizations-expanded">
                                      {item.customizations.fields && item.customizations.fields.slice(2).map((field, idx) => (
                                        <div key={`expanded-${idx}`} className="custom-row">
                                          <span className="custom-label">{field.name}:</span>
                                          <span className="custom-value">{field.selected_value || 'N/A'}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {hiddenCount > 0 && (
                                    <button
                                      className="btn-view-more"
                                      onClick={() => toggleExpandCustomizations(item.wishlist_item_id)}
                                    >
                                      {isExpanded ? (
                                        <>
                                          <i className="ph ph-caret-up"></i>
                                          View Less
                                        </>
                                      ) : (
                                        <>
                                          <i className="ph ph-caret-down"></i>
                                          +{hiddenCount} more
                                        </>
                                      )}
                                    </button>
                                  )}
                                </>
                              ) : (
                                <p className="text-gray-500">No customizations</p>
                              )}
                            </div>
                          </td>

                          <td className="px-40 py-32 border-end border-neutral-100">
                            <div className="price-breakdown">
                              <div className="price-row">
                                <span className="price-label">Base:</span>
                                <span className="price-value">${parseFloat(item.prices?.base_price || 0).toFixed(2)}</span>
                              </div>
                              <div className="price-row">
                                <span className="price-label">Material:</span>
                                <span className="price-value">${parseFloat(item.prices?.material_price || 0).toFixed(2)}</span>
                              </div>
                              {parseFloat(item.prices?.customization_price || 0) > 0 && (
                                <div className="price-row">
                                  <span className="price-label">Customization:</span>
                                  <span className="price-value">${parseFloat(item.prices?.customization_price || 0).toFixed(2)}</span>
                                </div>
                              )}
                              <div className="price-row price-total">
                                <span className="price-label fw-bold">Total:</span>
                                <span className="price-value fw-bold">${parseFloat(item.prices?.total_price || 0).toFixed(2)}</span>
                              </div>
                            </div>
                          </td>
                          {/* ✅ NEW: Customization Image Upload Column */}
                          <td className="px-40 py-32 border-end border-neutral-100">
                            <div className="customization-image-upload">
                              <input
                                type="file"
                                id={`image-upload-${item.wishlist_item_id}`}
                                accept="image/*"
                                onChange={(e) => handleImageChange(e, item.wishlist_item_id)}
                                style={{ display: 'none' }}
                              />

                              {!imageData ? (
                                <label
                                  htmlFor={`image-upload-${item.wishlist_item_id}`}
                                  style={{
                                    display: 'block',
                                    padding: '15px',
                                    border: '2px dashed #90caf9',
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: '#ffffff',
                                    minWidth: '120px'
                                  }}
                                >
                                  <i className="ph ph-cloud-arrow-up" style={{ fontSize: '1.5rem', color: '#1976d2', display: 'block', marginBottom: '5px' }}></i>
                                  <small style={{ color: '#666', fontSize: '0.75rem' }}>Upload Image</small>
                                </label>
                              ) : (
                                <div style={{ position: 'relative', textAlign: 'center' }}>
                                  <img
                                    src={imageData.preview}
                                    alt="Preview"
                                    style={{
                                      maxWidth: '120px',
                                      maxHeight: '120px',
                                      borderRadius: '8px',
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                  />
                                  <button
                                    onClick={() => removeItemImage(item.wishlist_item_id)}
                                    type="button"
                                    style={{
                                      position: 'absolute',
                                      top: '5px',
                                      right: '5px',
                                      backgroundColor: '#f44336',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '24px',
                                      height: '24px',
                                      cursor: 'pointer',
                                      fontSize: '1rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    ×
                                  </button>
                                  {isUploading && (
                                    <div style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      backgroundColor: 'rgba(0,0,0,0.5)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '8px'
                                    }}>
                                      <small style={{ color: 'white' }}>Uploading...</small>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="px-40 py-32">
                            <div className="d-flex align-items-center gap-16 flex-wrap">
                              <button
                                onClick={() => editItem(item)}
                                className="btn btn-outline-main rounded-8 px-24"
                                title="Edit customizations"
                              >
                                <i className="ph ph-pencil" /> Edit
                              </button>
                              <button
                                onClick={() => addToCart(item)}
                                className="btn btn-outline-main rounded-8 px-24"
                                title="Add to cart"
                                disabled={isUploading}
                              >
                                <i className="ph ph-shopping-cart" /> {isUploading ? 'Wait...' : 'Cart'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row mt-32">
              <div className="col-lg-4 ms-auto">
                <div className="border border-gray-100 rounded-8 px-40 py-32">
                  <div className="d-flex justify-content-between align-items-center mb-24 pb-24 border-bottom border-neutral-100">
                    <span className="text-lg fw-semibold">Subtotal ({wishlistItems.length} items):</span>
                    <span className="text-lg h6 mb-0 fw-bold text-orange-600">${totalPrice.toFixed(2)}</span>
                  </div>
                  <button
                    onClick={addAllToCart}
                    className="btn btn-main w-100 rounded-8"
                    disabled={Object.values(uploadingImages).some(val => val)}
                  >
                    Add All to Cart <i className="ph ph-shopping-cart" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WishListSection;
